import React, { ReactElement } from "react";

import { parseHtml } from "@use/parse-html";

import { elementAttributes } from "./config";
import { isElementNode, isTextNode, parseHtmlElement } from "./utils";

/**
 * Internally auto incremented id assigned to created React nodes, so that
 * they'll always have a unique key (suppresses warnings about missing keys!)
 */
let autoIncrementedNodeId = 0;

/**
 * Specification of available options when running the parse method.
 */
export type ParseOptions = {
  /**
   * By default this parser will attempt parsing text/html, but if you need
   * another mimeType it can be supplied here.
   */
  mimeType?: DOMParserSupportedType;

  /**
   * If enabled, then html-entities decoding will be run on the input before
   * executing any form of parsing.
   */
  decodeHTMLEntities?: boolean;

  /**
   * If enabled, then all built-in parsers will simply ignore output from
   * parsers that return promises.
   */
  sync?: boolean;

  /**
   * If enabled, then warnings about unsupported tags will not be logged to
   * the console.
   */
  quiet?: boolean;
};

/**
 * Describes the signature of custom tag parsers callbacks, which can be used to
 * allow fully customised tags.
 */
export type ParserCallback = (
  element: Element,
  parser: ReactHtmlParser,
  options: ParseOptions
) => React.ReactNode | Promise<React.ReactNode | null> | null;

/**
 * The HTML parser is used to convert an html-based input into an array of React
 * nodes, which can be rendered directly in JSX.
 *
 * By default all known HTML tags, attributes and styles are allowed, but you
 * can opt in/out of certain things by using white-/blacklisting. Further it is
 * possible to define fully customised parsers of custom tags.
 *
 * ```ts
 * const parser = new ReactHtmlParser();
 *
 * parser.addParser("video", (elem: Element) => {
 *     return <Video src={ elem.getAttribute("src") } />;
 * });
 * ```
 */
export class ReactHtmlParser {
  /**
   * Contains an internal map of parsers that are used to render different
   * kinds of tags to JSX. By default all html-tags will be allowed.
   */
  private parsers: { [key: string]: ParserCallback | null } = {};

  /**
   * Optional map of blacklisted html tags
   */
  private blacklistedTags?: { [key: string]: true };

  /**
   * Optional map of blacklisted html attributes
   */
  private blacklistedAttrs?: { [key: string]: true };

  /**
   * Optionally specifies that text nodes are blacklisted, and therefore
   * should be filtered away.
   */
  private blacklistedTextNodes: boolean = false;

  /**
   * Optional map of whitelisted html tags
   */
  private whitelistedTags?: { [key: string]: true };

  /**
   * Optional map of whitelisted html attributes
   */
  private whitelistedAttrs?: { [key: string]: true };

  /**
   * Optionally specifies a tag name, which all pure text nodes should be
   * wrapped inside.
   */
  private textNodeWrapper?: string;

  /**
   * Set up default handlers for all known html tags when the parser is
   * instantiated.
   */
  constructor() {
    // Allow parsing of all elements by default
    Object.keys(elementAttributes).forEach((tag) => {
      this.addParser(tag, parseHtmlElement);
    });
  }

  /**
   * Use this method to map a parser to a specific kind of tag, which allows
   * you to build fully customised feeds that can easily be translated into
   * your own React components.
   *
   * ```tsx
   * const parser = new ReactHtmlParser();
   *
   * parser.addParser("Slideshow", (elem: Element) => {
   *     // Parse given list of images
   *     const images = Array.from(elem.childNodes)
   *         .filter(isElementNode)
   *         .filter((childNode) => childNode.tagName === "SlideshowImage")
   *         .map((childNode) => childNode.getAttribute("src"));
   *
   *     // Return custom slideshow component
   *     return <MySlideshow images={ images } />
   * });
   * ```
   */
  public addParser(tag: string, parser: ParserCallback): void {
    this.parsers[tag] = parser;
  }

  /**
   * Use this method to create a whitelist of allowed HTML tags, so that only
   * the given tags will be allowed by the parser. For instance, you could
   * choose only to allow <b>-tags in a text string, if we only want to
   * support bold elements.
   */
  public whitelistTags(...tags: string[]): void {
    // We cannot simultaneously black- and whitelist tags
    if (this.blacklistedTags) {
      throw new Error(
        "ReactHtmlParser.whitelistTags(): Cannot simultaneously black- and whitelist tags."
      );
    }

    this.whitelistedTags = this.convertArrayToMap(tags);
  }

  /**
   * Creates a whitelist of allowed HTML attributes, so that only the given
   * types of attributes will be allowed on plain HTML tags. For instance you
   * could choose to only allow class-attributes, if that's the only thing we
   * want to support.
   */
  public whitelistAttrs(...attrs: string[]): void {
    // We cannot simultaneously black- and whitelist tags
    if (this.blacklistedAttrs) {
      throw new Error(
        "ReactHtmlParser.whitelistAttrs(): Cannot simultaneously black- and whitelist attributes."
      );
    }

    this.whitelistedAttrs = this.convertArrayToMap(attrs);
  }

  /**
   * Reigsters that text nodes should be whitelisted (ie. that they should be
   * included in the parsed output).
   */
  public whitelistTextNodes(): void {
    this.blacklistedTextNodes = false;
  }

  /**
   * Creates a blacklist of disallowed HTML tags, so that the given list of
   * tags will not be allowed.
   */
  public blacklistTags(...tags: string[]): void {
    // We cannot simultaneously black- and whitelist tags
    if (this.whitelistedTags) {
      throw new Error(
        "ReactHtmlParser.blacklistTags(): Cannot simultaneously black- and whitelist tags."
      );
    }

    this.blacklistedTags = this.convertArrayToMap(tags);
  }

  /**
   * Creates a blacklist of disallowed HTML attributes, so that the given list
   * of attributes will not be allowed. For instance you could disable the use
   * of inline styling.
   */
  public blacklistAttrs(...attrs: string[]): void {
    // We cannot simultaneously black- and whitelist tags
    if (this.whitelistedAttrs) {
      throw new Error(
        "ReactHtmlParser.blacklistAttrs(): Cannot simultaneously black- and whitelist attributes."
      );
    }

    this.blacklistedAttrs = this.convertArrayToMap(attrs);
  }

  /**
   * Reigsters that text nodes should be blacklisted (ie. that we don't want
   * to include them in the parsed output).
   */
  public blacklistTextNodes(): void {
    this.blacklistedTextNodes = true;
  }

  /**
   * Registers that text nodes should be wrapped by the given kind of tag
   * when outputting parsed content.
   */
  public wrapTextNodes(tagName?: string): void {
    this.textNodeWrapper = tagName;
  }

  /**
   * Helper that determines if a given tag is allowed based on the available
   * white- or blacklist.
   */
  public isTagAllowed(tag: string): boolean {
    // If a whiteliste of tags is provided, then ensure that this tag is
    // available on it
    if (this.whitelistedTags) {
      return this.whitelistedTags[tag] === true;

      // If a blacklist of tags is provided, then ensure that this tag is
      // not on it
    } else if (this.blacklistedTags) {
      return this.blacklistedTags[tag] !== true;

      // If neither a whitelist nor a blacklist is provided, then all tags
      // are allowed
    } else {
      return true;
    }
  }

  /**
   * Helper that determines if a given attribute is allowed based on the
   * available white- or blacklist.
   */
  public isAttrAllowed(attr: string): boolean {
    // If a whitelist of attributes is provided, then ensure that this
    // attribute is available on it
    if (this.whitelistedAttrs) {
      return this.whitelistedAttrs[attr] === true;

      // If a blacklist of attributes is provided, then ensure that this
      // attribute is not on it
    } else if (this.blacklistedAttrs) {
      return this.blacklistedAttrs[attr] !== true;

      // If neither a whitelist nor a blacklist is provided, then all
      // attributes are allowed
    } else {
      return true;
    }
  }

  /**
   * Runs the given xml through all mapped parsers and returns the resulting
   * JSX elements.
   */
  public async parse(
    html: string,
    options: ParseOptions = {}
  ): Promise<React.ReactNode[]> {
    return this.parseChildren(
      parseHtml(html, options.mimeType, options.decodeHTMLEntities),
      options
    );
  }

  /**
   * You can use this method to parse out all children under a given node;
   * this is helpful if you need to allow children inside some of your custom
   * parsers.
   */
  public async parseChildren(
    node: Node,
    options: ParseOptions
  ): Promise<React.ReactNode[]> {
    return Promise.all(
      Array.from(node.childNodes).map(async (child) =>
        this.parseNode(child, options)
      )
    );
  }

  /**
   * Runs the given xml through all mapped parsers and returns the resulting
   * JSX elements (this method will ignore output from parsers that return
   * promises!)
   */
  public parseSync(
    html: string,
    options: ParseOptions = {}
  ): React.ReactNode[] {
    return this.parseChildrenSync(
      parseHtml(html, options.mimeType, options.decodeHTMLEntities),
      {
        ...options,
        sync: true,
      }
    );
  }

  /**
   * You can use this method to parse out all children under a given node;
   * this is helpful if you need to allow children inside some of your custom
   * parsers (this method will ignore output from parsers that return
   * promises!)
   */
  public parseChildrenSync(
    node: Node,
    options: ParseOptions
  ): React.ReactNode[] {
    return Array.from(node.childNodes).map((child) =>
      this.parseNodeSync(child, options)
    );
  }

  /**
   * Internal helper that translates an array of values to a map that's easily
   * accessible by the browser (there are multiple usecases where we would
   * rather do a lookup agains `map["value"] === true` than
   * `arr.contains("value")` for performance).
   */
  private convertArrayToMap(arr: string[]): { [key: string]: true } {
    // Prepare the output map
    const map: { [key: string]: true } = {};

    // Add all values to it
    for (const value of arr) {
      map[value] = true;
    }

    return map;
  }

  /**
   * Helper that'll parse all kinds of DOM nodes into their corresponding
   * React elements, regardless of node type.
   */
  private async parseNode(
    node: Node,
    options: ParseOptions
  ): Promise<React.ReactNode> {
    if (isElementNode(node)) {
      return this.parseElement(node, options);
    }

    if (isTextNode(node)) {
      return this.parseText(node, options);
    }

    return null;
  }

  /**
   * Helper that'll parse all kinds of DOM nodes into their corresponding
   * React elements, regardless of node type (this method will ignore output
   * from parsers that return promises!)
   */
  private parseNodeSync(node: Node, options: ParseOptions): React.ReactNode {
    if (isElementNode(node)) {
      return this.parseElementSync(node, options);
    }

    if (isTextNode(node)) {
      return this.parseText(node, options);
    }

    return null;
  }

  /**
   * Helper that'll parse out the content of a DOM text node (in this case, we
   * simply need to return the text content of the node directly back - React
   * will render the string just fine).
   */
  private parseText(
    node: Text,
    options: ParseOptions
  ): React.JSX.Element | string | null {
    // If text nodes are blacklisted, then ignore...
    if (this.blacklistedTextNodes) {
      // Log that text nodes are blacklisted, so that developers can
      // easily debug bad input
      if (process.env.NODE_ENV !== "production" && options.quiet !== true) {
        console.warn(
          "ReactHtmlParser.parseText(): " +
            `Text nodes have been blacklisted "${node.nodeValue}".`
        );
      }

      return null;
    }

    // If text nodes should be wrapped, then do so now
    if (this.textNodeWrapper !== undefined && (node.nodeValue || "").trim()) {
      return React.createElement(
        this.textNodeWrapper,
        {
          key: `reactHtmlParser-${++autoIncrementedNodeId}`,
        },
        node.nodeValue
      );
    }

    // ... Otherwise return as pure text
    return node.nodeValue;
  }

  /**
   * Helper that parses an element based on the available parsers (this is
   * where the real magic happens).
   */
  private async parseElement(
    node: Element,
    options: ParseOptions
  ): Promise<React.ReactNode> {
    const tag = node.nodeName.toLowerCase();
    const parser = this.parsers[tag];

    // If a parser was associated with the given tag, then parse it and
    // return the content directly
    if (parser) {
      let result: React.ReactNode | null = await parser(node, this, options);

      // Ensure that the result has a key applied, so that React will not
      // complain about using arrays with items without keys
      if (result && typeof result !== "string" && typeof result !== "number") {
        // If the result doesn't have a key, then clone it and inject an
        // auto-incremented ket
        if (!(result as ReactElement).key) {
          result = React.cloneElement((result as ReactElement), {
            key: `reactHtmlParser-${++autoIncrementedNodeId}`,
          });
        }
      }

      return result;
    }

    // ... Otherwise, if no parsers were associated, we have no way of
    // rendering the content of this node, so we'll just return null (which
    // results in nothingness in the UI)... On non-production environments
    // we'll log the issue to the console, so developers can handle this
    // accordingly
    if (process.env.NODE_ENV !== "production" && options.quiet !== true) {
      console.warn(
        "ReactHtmlParser.parseElement(): " +
          `There is no parser associated with the tag "${tag}".`
      );
    }

    return null;
  }

  /**
   * Helper that parses an element based on the available parsers (this is
   * where the real magic happens - this method will ignore output from
   * parsers that return promises!)
   */
  private parseElementSync(
    node: Element,
    options: ParseOptions
  ): React.ReactNode {
    const tag = node.nodeName.toLowerCase();
    const parser = this.parsers[tag];

    // If a parser was associated with the given tag, then parse it and
    // return the content directly
    if (parser) {
      let result: Promise<React.ReactNode | null> | React.ReactNode | null =
        parser(node, this, options);

      // Ignore the elemennt if the parser returned a promise!
      if (result instanceof Promise) {
        return null;
      }

      // Ensure that the result has a key applied, so that React will not
      // complain about using arrays with items without keys
      if (result && typeof result !== "string" && typeof result !== "number") {
        // If the result doesn't have a key, then clone it and inject an
        // auto-incremented ket
        if (!(result as ReactElement).key) {
          result = React.cloneElement((result as ReactElement), {
            key: `reactHtmlParser-${++autoIncrementedNodeId}`,
          });
        }
      }

      return result;
    }

    // ... Otherwise, if no parsers were associated, we have no way of
    // rendering the content of this node, so we'll just return null (which
    // results in nothingness in the UI)... On non-production environments
    // we'll log the issue to the console, so developers can handle this
    // accordingly
    if (process.env.NODE_ENV !== "production" && options.quiet !== true) {
      console.warn(
        "ReactHtmlParser.parseElement(): " +
          `There is no parser associated with the tag "${tag}".`
      );
    }

    return null;
  }
}
